import type { WeightSeries } from '@nutrigeist/shared';
import { formatLongDate, formatNumber } from '../lib/format.js';

/**
 * The two things a weigh-in history is actually for: how fast the smoothed
 * trend is moving, and — if a goal weight is set — when it is on course to get
 * there. Both come straight from the server's trend regression.
 */
export function WeightTrendSummary({ series }: { series: WeightSeries }): JSX.Element | null {
  const { ratePerWeekKg, projection } = series;
  if (ratePerWeekKg == null && projection == null) return null;

  const stable = ratePerWeekKg != null && Math.abs(ratePerWeekKg) < 0.005;

  // The server withholds a rate until there is enough history; say so plainly
  // rather than leaving the row empty or implying the weight is not moving.
  if (ratePerWeekKg == null) {
    return (
      <span className="hint">
        Noch zu wenige Messungen für eine Wochenrate — ab etwa einer Woche mit mehreren Wiegungen
        erscheint sie hier
        {projection
          ? ` samt Prognose für dein Ziel von ${formatNumber(projection.targetKg, 1)} kg`
          : ''}
        .
      </span>
    );
  }

  return (
    <div className="row" style={{ flexWrap: 'wrap', gap: 8 }}>
      <span className="badge">
        {stable
          ? 'stabil'
          : `${ratePerWeekKg < 0 ? '−' : '+'}${formatNumber(Math.abs(ratePerWeekKg), 2)} kg/Woche`}
      </span>

      {projection?.status === 'reached' && (
        <span className="badge badge-accent">
          Ziel {formatNumber(projection.targetKg, 1)} kg erreicht
        </span>
      )}

      {projection?.status === 'on_track' && projection.etaDate && (
        <span className="badge">
          Ziel {formatNumber(projection.targetKg, 1)} kg ~ {formatLongDate(projection.etaDate)}
        </span>
      )}

      {projection?.status === 'diverging' && (
        <span className="badge badge-warning">kein Trend Richtung Ziel</span>
      )}
    </div>
  );
}
